import { AloftPrice, isUndefined } from '@aloft/toolkit-shared';
import { Block, Flex, SimpleInput, Tooltip, Typography } from '@aloft/toolkit-sui';

import { forwardRef } from 'react';

import { useAppraisifyValidation } from '../../../../hooks/validation/useAppraisifyValidation.ts';
import { setOpinionOfSiteValue } from '../../../../redux/features/actions/setOpinionOfSiteValue.ts';
import { selectOpinionOfSiteValuePrice } from '../../../../redux/features/selectors/opinionOfSiteValueSelectors.ts';
import { selectNumericSiteArea } from '../../../../redux/features/selectors/propertyDataSelectors/siteSelectors.ts';
import { selectOpinionOfSiteValuePerSFDisplay } from '../../../../redux/features/selectors/selectOpinionOfSiteValuePerSF.ts';
import { setPerSf } from '../../../../redux/features/thunks/setPerSf.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { RootState } from '../../../../redux/types.ts';
import { schPrice } from '../../types.ts';

const TooltipLabel = () => (
  <Flex flexDirection="column" gap="1rem">
    <Typography variant="body1" color="textOffset">
      Determine the subject’s site value based on the extracted site values of comparables.
    </Typography>
    <Typography variant="body1" color="textOffset">
      In addition to land value, these site values include grading, drains, landscaping, paving, and
      utilities.
    </Typography>
  </Flex>
);

export const SubjectSite = forwardRef<HTMLInputElement>((_props, opinionOfSiteValueRef) => {
  const dispatch = useAppDispatch();
  const { errors } = useAppraisifyValidation();
  const { opinionOfSiteValue } = errors?.Analysis ?? undefined;

  const siteArea = useAppSelector((state: RootState) => selectNumericSiteArea(state, 'subject'));
  const siteValue = useAppSelector(selectOpinionOfSiteValuePrice);
  const perSf = useAppSelector(selectOpinionOfSiteValuePerSFDisplay);

  const handleSiteValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = schPrice.safeParse(event.target.value);
    const parsedValue = value.success
      ? (value.data ?? AloftPrice.zero.toJSON())
      : AloftPrice.zero.toJSON();

    dispatch(setOpinionOfSiteValue(parsedValue));

    if (parsedValue.unitAmount > 0) {
      (event.nativeEvent.target as any)?.setCustomValidity('');
      (event.nativeEvent.target as any)?.reportValidity();
    }
  };

  const handlePerSfChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = schPrice.safeParse(event.target.value);
    const parsedValue = value.success
      ? (value.data ?? AloftPrice.zero.toJSON())
      : AloftPrice.zero.toJSON();

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    dispatch(setPerSf(parsedValue));
  };

  return (
    <Flex justifyContent="space-between" gap="1rem">
      <Block width="100%">
        <Tooltip label={<TooltipLabel />} placement="top" openDelay={2000} width="100%">
          <SimpleInput
            ref={opinionOfSiteValueRef}
            id="siteValue"
            onChange={handleSiteValueChange}
            required
            type="number"
            label="Opinion of Site Value"
            labelTypographyVariant="h4"
            precision={0}
            size="sm"
            prefix="$"
            useSeparator
            width="100%"

            value={siteValue?.toString(true, true)}
            invalid={!!opinionOfSiteValue}
            errorMessage={opinionOfSiteValue ? opinionOfSiteValue?.message : undefined}
          />
        </Tooltip>
      </Block>
      <Tooltip label={<TooltipLabel />} placement="top" openDelay={2000}>
        <SimpleInput
          id="perSf"
          onChange={handlePerSfChange}
          value={perSf}
          required
          type="number"
          label="$/sf"
          labelTypographyVariant="h4"
          precision={2}
          size="sm"
          useSeparator
          width="5rem"
          disabled={isUndefined(siteArea)}
        />
      </Tooltip>
    </Flex>
  );
});
